import React, { useRef, useEffect } from 'react';
import { Box, TextField, Typography, Chip, IconButton, Grid, MenuItem } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CampaignSipTrunkCreateRequestDto,
  DefaultSipSettingsDto,
} from '@shared/services/apiService/apiService';
import { CampaignTypeEnum, SipTransportEnum } from '@shared/constants/campaign.constants';

interface SipSettingsFormProps {
  settings: CampaignSipTrunkCreateRequestDto | null;
  onChange: (settings: CampaignSipTrunkCreateRequestDto | null) => void;
  onValidationChange?: (isValid: boolean) => void;
  type: 'inbound' | 'outbound';
  disabled?: boolean;
  /** Hide the section title when the parent already provides one (e.g. campaign Sip Trunk tab). */
  hideTitle?: boolean;
}

const CIDR_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\/([0-9]|[1-2][0-9]|3[0-2])$/;

export const SipSettingsForm: React.FC<SipSettingsFormProps> = ({
  settings,
  onChange,
  onValidationChange,
  type,
  disabled = false,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const allowedAddressInputRef = useRef<HTMLInputElement>(null);
  const [currentInputValue, setCurrentInputValue] = React.useState('');
  const [inputError, setInputError] = React.useState<string | undefined>();
  const [allowedAddressInputValue, setAllowedAddressInputValue] = React.useState('');
  const [allowedAddressInputError, setAllowedAddressInputError] = React.useState<
    string | undefined
  >();

  const isOutbound = type === CampaignTypeEnum.OUTBOUND;

  const phoneNumberRegex =
    /^(?:\+?[0-9]{2,14}|sips?:[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+(?::\d+)?(?:;[A-Za-z0-9=]+)*|tel:[0-9]+)$/;

  const hasAnySettings =
    settings &&
    (settings.authUsername?.trim() ||
      settings.authPassword?.trim() ||
      settings.address?.trim() ||
      (settings.numbers && settings.numbers.length > 0) ||
      (settings.allowedAddresses && settings.allowedAddresses.length > 0));

  const anySipFieldEntered =
    settings &&
    (settings.authUsername?.trim() ||
      settings.authPassword?.trim() ||
      settings.address?.trim() ||
      (settings.numbers && settings.numbers.length > 0) ||
      (settings.allowedAddresses && settings.allowedAddresses.length > 0));

  const phoneNumbersRequired = anySipFieldEntered;
  const addressRequired = isOutbound && anySipFieldEntered;

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumberRegex.test(phoneNumber)) {
      return false;
    }

    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

    if (/^(\d)\1+$/.test(numberPart)) {
      return false;
    }

    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return false;
    }

    return true;
  };

  const validateCidr = (cidr: string): boolean => {
    return CIDR_REGEX.test(cidr);
  };

  const getFieldError = (field: keyof DefaultSipSettingsDto): string | undefined => {
    if (!hasAnySettings) return undefined;

    switch (field) {
      case 'address':
        if (addressRequired && !settings?.address?.trim()) {
          return t('superAdminMainPage.companies.sipSettings.addressRequired');
        }
        break;
      case 'numbers':
        if (phoneNumbersRequired && (!settings?.numbers || settings.numbers.length === 0)) {
          return t('superAdminMainPage.companies.sipSettings.numbersRequired');
        }
        if (settings?.numbers && settings.numbers.length > 0) {
          const invalidNumber = settings.numbers.find((num) => !validatePhoneNumber(num));
          if (invalidNumber) {
            return t('superAdminMainPage.companies.sipSettings.invalidPhoneNumber');
          }
        }
        break;
    }
    return undefined;
  };

  const getPhoneNumberError = (phoneNumber: string): string | undefined => {
    if (!phoneNumberRegex.test(phoneNumber)) {
      return t('superAdminMainPage.companies.sipSettings.invalidPhoneNumberFormat');
    }

    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;
    if (/^(\d)\1+$/.test(numberPart)) {
      return t('superAdminMainPage.companies.sipSettings.phoneNumberCannotBeRepeated');
    }

    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return t('superAdminMainPage.companies.sipSettings.phoneNumberCannotBeSequential');
    }

    return undefined;
  };

  const getCidrError = (cidr: string): string | undefined => {
    if (!validateCidr(cidr)) {
      return t('superAdminMainPage.companies.sipSettings.invalidCidrFormat');
    }
    return undefined;
  };

  const hasValidationErrors = (): boolean => {
    if (!anySipFieldEntered) {
      return false;
    }

    if (isOutbound && addressRequired && !settings?.address?.trim()) {
      return true;
    }

    if (phoneNumbersRequired && (!settings?.numbers || settings.numbers.length === 0)) {
      return true;
    }

    if (settings?.numbers && settings.numbers.length > 0) {
      const hasInvalidNumber = settings.numbers.some((num) => !validatePhoneNumber(num));
      if (hasInvalidNumber) {
        return true;
      }
    }

    if (!isOutbound && settings?.allowedAddresses && settings.allowedAddresses.length > 0) {
      const hasInvalidCidr = settings.allowedAddresses.some((addr) => !validateCidr(addr));
      if (hasInvalidCidr) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = hasValidationErrors();
      onValidationChange(!hasErrors);
    }
  }, [settings, anySipFieldEntered, onValidationChange]);

  const handleFieldChange = (field: keyof CampaignSipTrunkCreateRequestDto, value: unknown) => {
    const newSettings: CampaignSipTrunkCreateRequestDto = {
      type,
      authUsername: isOutbound ? '' : undefined,
      authPassword: isOutbound ? '' : undefined,
      numbers: [],
      address: isOutbound ? '' : undefined,
      allowedAddresses: !isOutbound ? [] : undefined,
      transport: undefined,
      ...settings,
      [field]: value,
    };

    onChange(newSettings);
  };

  const handleNumberAdd = (number: string) => {
    const trimmedNumber = number.trim();
    if (trimmedNumber && !settings?.numbers.includes(trimmedNumber)) {
      if (!validatePhoneNumber(trimmedNumber)) {
        const errorMessage = getPhoneNumberError(trimmedNumber);
        console.warn('Invalid phone number:', trimmedNumber, errorMessage);
        return;
      }
      handleFieldChange('numbers', [...(settings?.numbers || []), trimmedNumber]);
    }
  };

  const handleNumberRemove = (index: number) => {
    const newNumbers = [...(settings?.numbers || [])];
    newNumbers.splice(index, 1);
    handleFieldChange('numbers', newNumbers);
  };

  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      handleNumberAdd(target.value);
      target.value = '';
      setCurrentInputValue('');
      setInputError(undefined);
    }
  };

  const handleAddNumberClick = () => {
    if (phoneNumberInputRef.current?.value) {
      handleNumberAdd(phoneNumberInputRef.current.value);
      phoneNumberInputRef.current.value = '';
      setCurrentInputValue('');
      setInputError(undefined);
    }
  };

  const handleAllowedAddressAdd = (address: string) => {
    const trimmedAddress = address.trim();
    if (trimmedAddress && !settings?.allowedAddresses?.includes(trimmedAddress)) {
      if (!validateCidr(trimmedAddress)) {
        console.warn('Invalid CIDR:', trimmedAddress);
        return;
      }
      handleFieldChange('allowedAddresses', [
        ...(settings?.allowedAddresses || []),
        trimmedAddress,
      ]);
    }
  };

  const handleAllowedAddressRemove = (index: number) => {
    const newAddresses = [...(settings?.allowedAddresses || [])];
    newAddresses.splice(index, 1);
    handleFieldChange('allowedAddresses', newAddresses);
  };

  const handleAllowedAddressKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      handleAllowedAddressAdd(target.value);
      target.value = '';
      setAllowedAddressInputValue('');
      setAllowedAddressInputError(undefined);
    }
  };

  const handleAddAllowedAddressClick = () => {
    if (allowedAddressInputRef.current?.value) {
      handleAllowedAddressAdd(allowedAddressInputRef.current.value);
      allowedAddressInputRef.current.value = '';
      setAllowedAddressInputValue('');
      setAllowedAddressInputError(undefined);
    }
  };

  const handleInputChange = (value: string) => {
    setCurrentInputValue(value);
    if (value.trim()) {
      setInputError(getPhoneNumberError(value.trim()));
    } else {
      setInputError(undefined);
    }
  };

  const handleAllowedAddressInputChange = (value: string) => {
    setAllowedAddressInputValue(value);
    if (value.trim()) {
      setAllowedAddressInputError(getCidrError(value.trim()));
    } else {
      setAllowedAddressInputError(undefined);
    }
  };

  return (
    <Box sx={{ p: hideTitle ? 0 : 2 }}>
      {!hideTitle ? (
        <Typography variant="h6" gutterBottom>
          {t('superAdminMainPage.companies.sipSettings.title', {
            type: type === 'inbound' ? 'Inbound' : 'Outbound',
          })}
        </Typography>
      ) : null}

      <Grid container spacing={2.5}>
        {isOutbound && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.authUsername')}
                value={settings?.authUsername || ''}
                onChange={(e) => handleFieldChange('authUsername', e.target.value)}
                disabled={disabled}
                autoComplete="new-username"
                name="authUsername"
                placeholder={t('superAdminMainPage.companies.sipSettings.authUsernamePlaceholder')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.authPassword')}
                type="password"
                value={settings?.authPassword || ''}
                onChange={(e) => handleFieldChange('authPassword', e.target.value)}
                disabled={disabled}
                autoComplete="new-password"
                name="authPassword"
                placeholder={t('superAdminMainPage.companies.sipSettings.authPasswordPlaceholder')}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.address')}
                value={settings?.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                disabled={disabled}
                placeholder={t('superAdminMainPage.companies.sipSettings.addressPlaceholder')}
                error={!!getFieldError('address')}
                helperText={getFieldError('address')}
                autoComplete="off"
                required={!!addressRequired}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label={t('superAdminMainPage.companies.sipSettings.transport')}
                value={settings?.transport || SipTransportEnum.AUTO}
                disabled={disabled}
                onChange={(e) => handleFieldChange('transport', e.target.value)}>
                {Object.values(SipTransportEnum).map((transport) => (
                  <MenuItem key={transport} value={transport}>
                    {transport}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        )}

        {!isOutbound && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('superAdminMainPage.companies.sipSettings.addAllowedAddress')}
              placeholder={t('superAdminMainPage.companies.sipSettings.allowedAddressPlaceholder')}
              onKeyPress={handleAllowedAddressKeyPress}
              disabled={disabled}
              inputRef={allowedAddressInputRef}
              value={allowedAddressInputValue}
              onChange={(e) => handleAllowedAddressInputChange(e.target.value)}
              error={!!allowedAddressInputError}
              helperText={allowedAddressInputError}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleAddAllowedAddressClick}
                    disabled={
                      disabled || !!allowedAddressInputError || !allowedAddressInputValue.trim()
                    }>
                    <AddIcon />
                  </IconButton>
                ),
              }}
            />

            {settings?.allowedAddresses && settings.allowedAddresses.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {settings.allowedAddresses.map((address, index) => (
                  <Chip
                    key={index}
                    label={address}
                    onDelete={() => handleAllowedAddressRemove(index)}
                    disabled={disabled}
                    color={validateCidr(address) ? 'primary' : 'error'}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={
              phoneNumbersRequired
                ? `${t('superAdminMainPage.companies.sipSettings.addPhoneNumber')} *`
                : t('superAdminMainPage.companies.sipSettings.addPhoneNumber')
            }
            placeholder={t('superAdminMainPage.companies.sipSettings.phoneNumberPlaceholder')}
            onKeyPress={handleNumberKeyPress}
            disabled={disabled}
            inputRef={phoneNumberInputRef}
            value={currentInputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            error={!!getFieldError('numbers') || !!inputError}
            helperText={getFieldError('numbers') || inputError}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleAddNumberClick}
                  disabled={disabled || !!inputError || !currentInputValue.trim()}>
                  <AddIcon />
                </IconButton>
              ),
            }}
          />

          {settings?.numbers && settings.numbers.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {settings.numbers.map((number, index) => (
                <Chip
                  key={index}
                  label={number}
                  onDelete={() => handleNumberRemove(index)}
                  disabled={disabled}
                  color={validatePhoneNumber(number) ? 'primary' : 'error'}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
